import { Injector } from '@angular/core';
import { OverlayPanel } from "primeng/overlaypanel";
import { LazyLoadedWidgets } from "src/app/app-routing-lazy";
import { ILazyComponentHandler } from 'src/app/models/smart-actions.models';
import { ContextDataService } from 'src/app/shared/context-data/context-data.service';
import { IContextDataHost } from 'src/app/shared/context-data/context-data.models';
import { BaseProductService } from 'src/app/services/+products/-base-product.service';
import { BaseProductStateModel } from "src/app/services/+products/base-product-state.models";
import { TaskServiceFactory, TaskServiceFactoryFn } from "src/app/services/task/task.service";
import { ITaskCommandWrapper } from "src/app/services/task/task.state.models";
import { Utilities } from "src/app/services/utilities";
import { LazyLoadersRegistrationService } from 'src/app/shared/lazy-loader/lazy-loaders-register.service';
import { ActionHandlersDIMapping } from "./app-actions-di.mapping";
import { IActionContext, IActionHandler } from './smart-icon.models';
import { skip, take } from 'rxjs/operators';

// Version that try to resolve the dependencies without any infomation about them coming from the backend
export const loadComponentActionHandlerV2: IActionHandler = {
    handle: (context) => {
        const containerContext = context.container?.parentDataContext();
        const actionMetadata = resolveDI(context.action.metadata, context.injector);
        let data = actionMetadata?.data ?? {};
        const dataDependenciesReferences: any = {};

        // Dependencies references
        const productService = data.productService;
        if (!productService) {
            dataDependenciesReferences.productService = (containerContext + '-service').toLowerCase();
            dataDependenciesReferences.target = (containerContext + '-service').toLowerCase();
        }
        const lazyLoaderHandler = data.lazyComponentHandler;
        if (!lazyLoaderHandler) {
            dataDependenciesReferences.lazyComponentHandler = (containerContext + '-dashboard-tile').toLowerCase();
        }
        // Dependencies references

        const dataDependencies = resolveDI(dataDependenciesReferences, context.injector);
        data = { ...data, ...dataDependencies };
        const lazyLoaderComponent = actionMetadata?.loader ?? data.lazyComponentHandler as ILazyComponentHandler;

        const component = actionMetadata?.component ?? LazyLoadedWidgets.COMPONENT_SETCASEOWNER;

        if (lazyLoaderComponent.setCurrentCustomView) {
            lazyLoaderComponent.setCurrentCustomView(component, data);
        }
    }
};

// Version use the references comming from the backend
export const loadComponentActionHandler: IActionHandler = {
    handle: (context) => {
        const actionMetadata = resolveDI(context.action.metadata, context.injector);
        const lazyLoaderComponent = actionMetadata.loader as ILazyComponentHandler;
        if (lazyLoaderComponent.setCurrentCustomView) {
            lazyLoaderComponent.setCurrentCustomView(actionMetadata.component, actionMetadata.data);
        }
    }
}

export const executeFunctionActionHandler: IActionHandler = {
    handle: (context) => {
        const containerContext = context.container?.parentDataContext();
        let actionMetadata = Utilities.cloneHard(context.action.metadata);

        const productService = actionMetadata?.context?.productService ?? actionMetadata?.context?.target;
        if (!productService) {
            actionMetadata.context ??= {}
            actionMetadata.context.productService = (containerContext + '-service').toLowerCase();
            actionMetadata.context.target = (containerContext + '-service').toLowerCase();
        }

        if (actionMetadata?.confirmation) {
            actionMetadata.confirmation.lazyComponentHandler ??= (containerContext + '-dashboard-tile').toLowerCase();
            actionMetadata = resolveDI(actionMetadata, context.injector);

            const lazyLoaderComponent = actionMetadata.confirmation.lazyComponentHandler as ILazyComponentHandler;
            const confirmationData = { ...actionMetadata.confirmation, sourceAction: actionMetadata };
            if (lazyLoaderComponent.setCurrentCustomView) {
                lazyLoaderComponent.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_CONFIRMATION, confirmationData);
            }
            return;
        }

        actionMetadata = resolveDI(actionMetadata, context.injector);

        const command = actionMetadata?.command;
        if (typeof (command) == 'function') {
            command({ sourceAction: actionMetadata });
        }
    }
};

export const loadContextDataActionHandler: IActionHandler = {
    handle: (context: IActionContext) => {

        const actionMetadata = context.action.metadata;
        const dataDependenciesReferences: any = {};

        // Dependencies references
        const containerContext = context.container?.parentDataContext();
        dataDependenciesReferences.dataHost = (containerContext + '-service').toLowerCase();
        const { dataHost } = resolveDI(dataDependenciesReferences, context.injector);
        // Dependencies references


        const contextService = context.injector.get(ContextDataService);
        if ((dataHost as IContextDataHost)?.setContext) {
            if (contextService.showing() == actionMetadata.dataContext?.name) {
                contextService.showPanel.set(false);
            } else {
                (dataHost as IContextDataHost)?.setContext(actionMetadata.dataContext);
            }
        }
    }
}

export const showOverlayPanelActionHandler: IActionHandler = {
    handle: (context: IActionContext) => {
        context.event.stopPropagation();
        const settings = (context.iconSettings ?? context.cornerSettings);
        if (settings.actions.some(action => action.metadata?.$disabledAutoHide)) {
            return;
        }
        settings.$selectedAction = context.action;
        const overlayHostPropertyName = 'htmlOverlayPanel';
        const htmlOverlayContentHostPropertyName = 'htmlOverlayContent';
        const dynamicOverlayContentHostPropertyName = 'dynamicOverlayContentId';
        const dynamicOverlayContentHostContextPropertyName = 'dynamicOverlayContentMetadata';

        const contentSettings = context.action.metadata;

        if (!context.container[overlayHostPropertyName]) {
            console.error(`Overlay panel host property '${overlayHostPropertyName}' not found on the container.`);
            return;
        }

        const overlayHostProperty: OverlayPanel = context.container[overlayHostPropertyName];
        const autoHide = context.action?.metadata?.autoHide ?? true;
        const setOverlayContent = () => {
            if (!autoHide) {
                overlayHostProperty.showCloseIcon = true;
            }
            context.container[htmlOverlayContentHostPropertyName] = undefined;
            context.container[dynamicOverlayContentHostPropertyName] = undefined;
            if (contentSettings?.content) {
                context.container[dynamicOverlayContentHostPropertyName] = contentSettings.content;
                context.container[dynamicOverlayContentHostContextPropertyName] = contentSettings.context;
            } else {
                context.container[htmlOverlayContentHostPropertyName] = contentSettings.html;
            }
        };

        const showOverlay = () => {
            let hide = () => (overlayHostProperty.hide(), true);
            if (!autoHide) {
                hide = () => false;
            }
            context.host.showOverlay(context.container.id, context.event as MouseEvent,
                () => {
                    setOverlayContent();
                    overlayHostProperty.show(context.event);
                }, hide);
        };

        if (overlayHostProperty.overlayVisible) {
            overlayHostProperty.hide();
            new Promise(resolve => setTimeout(resolve, 300)).then(() => {
                showOverlay();
            });
        } else {
            showOverlay();
        }

        // try add the hide overlay action if not already present
        const hideAction = settings.actions.find(action => action.type === 'hide-overlaypanel-action-handler');
        if (autoHide) {
            overlayHostProperty.showCloseIcon = false;
            if (!hideAction) {
                settings.actions.push({
                    event: 'MOUSELEAVE',
                    type: 'hide-overlaypanel-action-handler',
                    metadata: {
                        autoCreated: true,
                    }
                });
            }

        } else {
            (() => {
                const internalSettings = settings;
                context.action.metadata.$disabledAutoHide = true;
                overlayHostProperty.onHide.pipe(skip(1), take(1)).subscribe(() => {

                    context.container[htmlOverlayContentHostPropertyName] = undefined;
                    context.container[dynamicOverlayContentHostPropertyName] = undefined;
                    context.container[dynamicOverlayContentHostContextPropertyName] = undefined;

                    internalSettings.actions.filter(action => action.metadata?.$disabledAutoHide).forEach(action => {
                        action.metadata.$disabledAutoHide = undefined;
                    });

                    context.host.hideOverlay(context.container.id);

                });
            })();
        }
    }
}

export const hideOverlayPanelActionHandler: IActionHandler = {
    handle: (context: IActionContext) => {
        const settings = (context.iconSettings ?? context.cornerSettings);

        if (!settings.actions.some(action => action.metadata?.$disabledAutoHide)) {

            const htmlOverlay = (context.container['htmlOverlayPanel'] as OverlayPanel);
            if (htmlOverlay) {
                // htmlOverlay.hide();
                context.host.hideOverlay(context.container.id);
            }

            const lazyContentOverlay = (context.container['lazyContentOverlayPanel'] as OverlayPanel);
            if (lazyContentOverlay) {
                // lazyContentOverlay.hide();
                context.host.hideOverlay(context.container.id);

            }
        }
    }
}

export const commandActionHandler: IActionHandler = {
    handle: (context: IActionContext) => {
        context.event.stopPropagation();
        const taskServiceFn = context.injector.get<TaskServiceFactoryFn>(TaskServiceFactory);
        const taskService = taskServiceFn().setContext();
        taskService.activate();
        const taskWrapper = Utilities.cloneHard<ITaskCommandWrapper>(context.action.metadata);
        taskService.host.runCommands([taskWrapper])
    }
};

export async function caseAdvanceStageActionExecutor(context: any) {
    const actionContext = context.sourceAction.context;
    const productServiceObj = (actionContext.target as BaseProductService<BaseProductStateModel>);
    await productServiceObj.advanceStage(actionContext?.action ?? 'Next', actionContext?.reason, actionContext?.metadata);
    return null;
}

const resolveDI = (object: any, injector: Injector, lazyLoaderRegistrationService?: LazyLoadersRegistrationService) => {
    if (!object) {
        return object;
    }

    lazyLoaderRegistrationService ??= injector.get(LazyLoadersRegistrationService);
    const result: any = {};
    if (Object.keys(object)) {
        const data = object ?? {};
        Object.keys(object).forEach(key => {
            if (Array.isArray(data[key])) {
                result[key] = [];
                for (let i = 0; i < object[key].length; i++) {
                    result[key][i] = resolveDI(object[key][i], injector, lazyLoaderRegistrationService);
                }
            } else if (typeof object[key] === 'object' && object[key] !== null) {
                result[key] = resolveDI(object[key], injector, lazyLoaderRegistrationService);
            } else {
                try {
                    result[key] = injector.get(ActionHandlersDIMapping.get(object[key]));
                }
                catch {
                    result[key] = lazyLoaderRegistrationService.getLoader(object[key]) ?? object[key];
                }
            }
        });
    }

    return result;
}
